/**
 * Evaluations page — `/evals`.
 *
 * Two-level view driven by the `workflowId` search param:
 *
 *   Level 1 (no workflowId): Org overview — KPI cards aggregated across all
 *   workflows, entity type dropdown, date range filter, one row per workflow.
 *
 *   Level 2 (?workflowId=X): Workflow evals — KPI cards scoped to one
 *   workflow, filter by suite/status, one row per eval run.
 *
 *   Level 3 is the eval detail page (separate route, already built).
 */
import { useClient } from '@cortex/react-query';
import type { EvaluationListItem, WorkflowListItem, WorkflowVersion } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { PageHeader } from '../../components/common/PageHeader';
import { Spinner } from '../../components/common/Spinner';
import { TagEditor } from '../../components/common/TagEditor';
import { routes } from '../../lib/constants';
import { formatDateTime, formatDuration } from '../../lib/format';
import { tdStyle, thStyle } from '../../lib/table-styles';
import { useUpdateWorkflowTags } from '../../lib/use-workflow-tags';

// ── Route definition ────────────────────────────────────────────────────────

export const Route = createFileRoute('/evals/')({
  component: EvalsPage,
  validateSearch: (raw: Record<string, unknown>): { workflowId?: string } => ({
    workflowId: typeof raw.workflowId === 'string' ? raw.workflowId : undefined,
  }),
});

// ── Shared types ────────────────────────────────────────────────────────────

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

interface IWorkflowGroup {
  workflowId: string;
  workflowName: string;
  /** The workflow's grouping tags (source of truth on the Workflow entity). */
  tags: string[];
  evaluations: EvaluationListItem[];
  latestPassRate: number | null;
  latestPassAt1: number | null;
  avgPassRate: number | null;
  totalRuns: number;
  suiteCount: number;
  latestStatus: string | null;
  latestCreatedAt: Date | null;
  timeline: Array<{ id: string; passRate: number | null }>;
}

type DateRange = '7d' | '30d' | '90d' | 'all';

// ── Data extraction helpers ─────────────────────────────────────────────────
// With the slim list endpoint, these fields are pre-computed server-side.
// Falls back to parsing scoreSummary if the API hasn't been upgraded yet.

function extractPassRate(e: EvaluationListItem): number | null {
  if (e.passRate != null) return e.passRate;
  // Fallback: parse from scoreSummary (old API response)
  const summary = (e as unknown as Record<string, unknown>).scoreSummary as Record<
    string,
    unknown
  > | null;
  if (!summary) return null;
  const taskResults = summary.taskResults;
  if (Array.isArray(taskResults) && taskResults.length > 0) {
    const passed = (taskResults as Array<{ passedTrials: number; nTrials: number }>).filter(
      (tr) => tr.passedTrials === tr.nTrials,
    ).length;
    return passed / taskResults.length;
  }
  const total = typeof summary.total === 'number' ? (summary.total as number) : 0;
  const passed = typeof summary.passed === 'number' ? (summary.passed as number) : 0;
  return total > 0 ? passed / total : null;
}

function extractPassAt1(e: EvaluationListItem): number | null {
  if (e.passAt1 != null) return e.passAt1;
  // Fallback: parse from scoreSummary
  const summary = (e as unknown as Record<string, unknown>).scoreSummary as Record<
    string,
    unknown
  > | null;
  if (!summary) return null;
  const agg = summary.aggregate as Record<string, { meanPassAtK?: number }> | undefined;
  return agg?.['1']?.meanPassAtK ?? null;
}

function extractTaskCount(e: EvaluationListItem): { passed: number; total: number } | null {
  if (e.totalCount != null && e.totalCount > 0) {
    return { passed: e.passedCount ?? 0, total: e.totalCount };
  }
  // Fallback: parse from scoreSummary
  const summary = (e as unknown as Record<string, unknown>).scoreSummary as Record<
    string,
    unknown
  > | null;
  if (!summary) return null;
  const taskResults = summary.taskResults;
  if (Array.isArray(taskResults) && taskResults.length > 0) {
    const passed = (taskResults as Array<{ passedTrials: number; nTrials: number }>).filter(
      (tr) => tr.passedTrials === tr.nTrials,
    ).length;
    return { passed, total: taskResults.length };
  }
  const total = typeof summary.total === 'number' ? (summary.total as number) : 0;
  const passed = typeof summary.passed === 'number' ? (summary.passed as number) : 0;
  if (total > 0) return { passed, total };
  return null;
}

function getSuiteId(e: EvaluationListItem): string | null {
  if (e.suiteId != null) return e.suiteId;
  // Fallback: old API may have suiteId on the raw response
  const sid = (e as unknown as Record<string, unknown>).suiteId;
  return typeof sid === 'string' ? sid : null;
}

function filterByDateRange(evals: EvaluationListItem[], range: DateRange): EvaluationListItem[] {
  if (range === 'all') return evals;
  const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return evals.filter((e) => new Date(e.createdAt).getTime() >= cutoff);
}

// ── Display helpers ─────────────────────────────────────────────────────────

function formatPct(v: number | null): string {
  if (v === null) return '—';
  return `${(v * 100).toFixed(0)}%`;
}

function formatScore(v: number | null): string {
  if (v === null) return '—';
  return v.toFixed(2);
}

function passRateColor(v: number | null): string {
  if (v === null) return 'var(--text-muted)';
  if (v >= 0.9) return 'var(--status-success)';
  if (v >= 0.5) return 'var(--terminal-amber)';
  return 'var(--status-danger)';
}

function MiniSparkline({ values }: { values: Array<number | null> }) {
  const w = 56;
  const h = 20;
  const n = values.length;
  if (n === 0) return null;
  const gap = 1;
  const barW = Math.max(2, Math.floor((w - gap * (n - 1)) / n));
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }}>
      {values.map((v, i) => {
        const barH = v === null ? 2 : Math.max(2, Math.round(v * h));
        return (
          <rect
            key={i}
            x={i * (barW + gap)}
            y={h - barH}
            width={barW}
            height={barH}
            fill={passRateColor(v)}
            rx={1}
          />
        );
      })}
    </svg>
  );
}

// ── Trend chart ─────────────────────────────────────────────────────────────

interface ITrendPoint {
  date: Date;
  value: number | null;
  label?: string;
}

interface ITrendSeries {
  name: string;
  color: string;
  points: ITrendPoint[];
}

function TrendChart({
  series,
  title,
  height = 160,
  gateThreshold,
}: {
  series: ITrendSeries[];
  title: string;
  height?: number;
  gateThreshold?: number;
}) {
  const allPoints = series.flatMap((s) => s.points);
  if (allPoints.length === 0) return null;

  const w = 700;
  const h = height;
  const padL = 50;
  const padR = 20;
  const padT = 16;
  const padB = 30;
  const plotW = w - padL - padR;
  const plotH = h - padT - padB;

  const dates = allPoints.map((p) => p.date.getTime());
  const minDate = Math.min(...dates);
  const maxDate = Math.max(...dates);
  const dateRange = maxDate - minDate || 1;

  const toX = (d: Date) => padL + ((d.getTime() - minDate) / dateRange) * plotW;
  const toY = (v: number) => padT + (1 - v) * plotH;

  return (
    <div className="card" style={{ padding: '20px 24px', marginBottom: 20 }}>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 9,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'var(--fog-green)',
          marginBottom: 14,
        }}
      >
        {title}
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} style={{ overflow: 'visible' }}>
        {/* Grid lines */}
        <line x1={padL} y1={padT} x2={padL} y2={padT + plotH} stroke="var(--border-subtle)" />
        <line
          x1={padL}
          y1={padT + plotH}
          x2={padL + plotW}
          y2={padT + plotH}
          stroke="var(--border-subtle)"
        />
        <line
          x1={padL}
          y1={toY(0.5)}
          x2={padL + plotW}
          y2={toY(0.5)}
          stroke="var(--border-subtle)"
          strokeWidth={0.5}
          strokeDasharray="4,4"
        />
        <line
          x1={padL}
          y1={padT}
          x2={padL + plotW}
          y2={padT}
          stroke="var(--border-subtle)"
          strokeWidth={0.5}
          strokeDasharray="4,4"
        />
        {/* Y-axis labels */}
        <text
          x={padL - 8}
          y={padT + plotH + 4}
          fill="var(--text-muted)"
          fontSize={10}
          textAnchor="end"
          fontFamily="var(--font-mono)"
        >
          0%
        </text>
        <text
          x={padL - 8}
          y={toY(0.5) + 4}
          fill="var(--text-muted)"
          fontSize={10}
          textAnchor="end"
          fontFamily="var(--font-mono)"
        >
          50%
        </text>
        <text
          x={padL - 8}
          y={padT + 4}
          fill="var(--text-muted)"
          fontSize={10}
          textAnchor="end"
          fontFamily="var(--font-mono)"
        >
          100%
        </text>
        {/* Gate threshold */}
        {gateThreshold != null && (
          <>
            <line
              x1={padL}
              y1={toY(gateThreshold)}
              x2={padL + plotW}
              y2={toY(gateThreshold)}
              stroke="var(--terminal-amber)"
              strokeWidth={1}
              strokeDasharray="6,4"
              opacity={0.7}
            />
            <text
              x={padL + plotW + 4}
              y={toY(gateThreshold) + 4}
              fill="var(--terminal-amber)"
              fontSize={9}
              fontFamily="var(--font-mono)"
              letterSpacing="0.06em"
            >
              {(gateThreshold * 100).toFixed(0)}% GATE
            </text>
          </>
        )}
        {/* X-axis date labels */}
        {(() => {
          const labelCount = Math.min(5, allPoints.length);
          if (labelCount < 2) return null;
          const labels: Array<{ x: number; text: string }> = [];
          for (let i = 0; i < labelCount; i++) {
            const t = minDate + (dateRange / (labelCount - 1)) * i;
            const d = new Date(t);
            const text = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            labels.push({ x: padL + (i / (labelCount - 1)) * plotW, text });
          }
          return labels.map((l, i) => (
            <text
              key={i}
              x={l.x}
              y={padT + plotH + 18}
              fill="var(--text-muted)"
              fontSize={10}
              textAnchor="middle"
              fontFamily="var(--font-mono)"
            >
              {l.text}
            </text>
          ));
        })()}
        {/* Series lines + dots */}
        {series.map((s) => {
          const validPoints = s.points.filter((p) => p.value !== null) as Array<{
            date: Date;
            value: number;
          }>;
          if (validPoints.length < 2) return null;
          const polyline = validPoints.map((p) => `${toX(p.date)},${toY(p.value)}`).join(' ');
          return (
            <g key={s.name}>
              <polyline
                points={polyline}
                fill="none"
                stroke={s.color}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {validPoints.map((p, i) => (
                <circle key={i} cx={toX(p.date)} cy={toY(p.value)} r={3} fill={s.color}>
                  <title>
                    {p.date.toLocaleDateString()} — {(p.value * 100).toFixed(0)}%
                  </title>
                </circle>
              ))}
            </g>
          );
        })}
      </svg>
      {/* Legend */}
      <div
        style={{
          display: 'flex',
          gap: 20,
          marginTop: 10,
          fontSize: 11,
          color: 'var(--text-secondary)',
        }}
      >
        {series.map((s) => (
          <span key={s.name}>
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: s.color,
                marginRight: 5,
                verticalAlign: 'middle',
              }}
            />
            {s.name}
          </span>
        ))}
        {gateThreshold != null && (
          <span style={{ color: 'var(--terminal-amber)' }}>
            — {(gateThreshold * 100).toFixed(0)}% gate threshold
          </span>
        )}
      </div>
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────

function EvalsPage() {
  const { workflowId } = Route.useSearch();
  return workflowId ? <WorkflowEvalsView workflowId={workflowId} /> : <OrgOverview />;
}

// ═══════════════════════════════════════════════════════════════════════════
// LEVEL 1 — Org Overview
// ═══════════════════════════════════════════════════════════════════════════

type EntityType = 'all' | 'workflows' | 'agents';

function OrgOverview() {
  const client = useClient();
  // All-time by default so entities aren't hidden once their last run ages
  // past 30 days (eval runs are infrequent). Narrowable via the period control.
  const [dateRange, setDateRange] = useState<DateRange>('all');
  const [entityType, setEntityType] = useState<EntityType>('all');
  // Multi-select tag filter (ANY semantics — a row shows if it carries any of
  // the selected tags) + a "group by tag" sectioned layout toggle.
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [groupByTag, setGroupByTag] = useState(false);
  const updateTags = useUpdateWorkflowTags();

  const list = useQuery({
    queryKey: ['evaluations'],
    queryFn: () =>
      client.http.get('v1/evaluations?limit=200').json<IListEnvelope<EvaluationListItem>>(),
  });
  const workflows = useQuery({
    queryKey: ['workflows', 'name-lookup'],
    queryFn: () =>
      client.http.get('v1/workflows?limit=200').json<IListEnvelope<WorkflowListItem>>(),
  });
  const workflowName = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of workflows.data?.items ?? []) m.set(w.id, w.name);
    return m;
  }, [workflows.data]);
  // workflowId → tags. The Workflow entity is the source of truth for the tags
  // we group / filter evals by (the `v1/workflows` lookup now returns them).
  const workflowTags = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const w of workflows.data?.items ?? []) m.set(w.id, w.tags ?? []);
    return m;
  }, [workflows.data]);

  // Filter by date range
  const evalsInRange = useMemo(
    () => filterByDateRange(list.data?.items ?? [], dateRange),
    [list.data, dateRange],
  );

  // Group evals by workflow. KG- and MEMORY-target evals have no carrier
  // workflow — each buckets under its own synthetic key so the grouping
  // stays string-safe and the two kinds never mix (their metrics are not
  // comparable). (Keys are module-scoped above.)
  const groups = useMemo<IWorkflowGroup[]>(() => {
    const byWorkflow = new Map<string, EvaluationListItem[]>();
    for (const e of evalsInRange) {
      const key = e.workflowId ?? (e.targetKind === 'MEMORY' ? MEMORY_GROUP_KEY : KG_GROUP_KEY);
      const arr = byWorkflow.get(key) ?? [];
      arr.push(e);
      byWorkflow.set(key, arr);
    }
    const out: IWorkflowGroup[] = [];
    for (const [wfId, evals] of byWorkflow) {
      evals.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      let scored = 0;
      let passRateSum = 0;
      let latestPassRate: number | null = null;
      let latestPassAt1: number | null = null;
      const timeline: Array<{ id: string; passRate: number | null }> = [];
      const suiteIds = new Set<string>();
      for (const e of [...evals].reverse()) {
        const pr = extractPassRate(e);
        timeline.push({ id: e.id, passRate: pr });
        if (pr !== null) {
          scored++;
          passRateSum += pr;
          latestPassRate = pr;
        }
        const pa1 = extractPassAt1(e);
        if (pa1 !== null) latestPassAt1 = pa1;
        const sid = getSuiteId(e);
        if (sid) suiteIds.add(sid);
      }
      out.push({
        workflowId: wfId,
        workflowName: workflowName.get(wfId) ?? '',
        // Workflow-level tags are the source of truth for grouping/filtering.
        tags: workflowTags.get(wfId) ?? [],
        evaluations: evals,
        latestPassRate,
        latestPassAt1,
        avgPassRate: scored > 0 ? passRateSum / scored : null,
        totalRuns: evals.length,
        suiteCount: suiteIds.size,
        latestStatus: evals[0]?.status ?? null,
        latestCreatedAt: evals[0] ? new Date(evals[0].createdAt) : null,
        timeline,
      });
    }
    out.sort((a, b) => {
      const aTime = a.latestCreatedAt?.getTime() ?? 0;
      const bTime = b.latestCreatedAt?.getTime() ?? 0;
      return bTime - aTime;
    });
    return out;
  }, [evalsInRange, workflowName, workflowTags]);

  // Org-wide KPIs
  const orgKpis = useMemo(() => {
    const passRates = groups.map((g) => g.latestPassRate).filter((v): v is number => v !== null);
    const overall =
      passRates.length > 0 ? passRates.reduce((a, b) => a + b, 0) / passRates.length : null;
    const totalRuns = groups.reduce((sum, g) => sum + g.totalRuns, 0);
    // Runs in last 7 days (always 7d regardless of date range filter)
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const allItems = list.data?.items ?? [];
    const runs7d = allItems.filter((e) => new Date(e.createdAt).getTime() >= sevenDaysAgo).length;
    // Regressions = failed gate evals in last 7d
    const regressions7d = allItems.filter(
      (e) =>
        new Date(e.createdAt).getTime() >= sevenDaysAgo &&
        e.status === 'COMPLETED' &&
        extractPassRate(e) !== null &&
        (extractPassRate(e) ?? 1) < 0.9,
    ).length;
    return { overall, totalRuns, workflows: groups.length, runs7d, regressions7d };
  }, [groups, list.data]);

  // Every distinct tag across the org's workflows, for the filter bar.
  const allTags = useMemo(() => {
    const s = new Set<string>();
    for (const tags of workflowTags.values()) for (const t of tags) s.add(t);
    return [...s].sort();
  }, [workflowTags]);

  const toggleTag = (tag: string) =>
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );

  // entity-type filter → tag filter (ANY of the selected tags).
  const visibleGroups = useMemo(() => {
    const byEntity = entityType === 'agents' ? [] : groups;
    if (selectedTags.length === 0) return byEntity;
    return byEntity.filter((g) => g.tags.some((t) => selectedTags.includes(t)));
  }, [groups, entityType, selectedTags]);

  const onEditTags = (workflowId: string, next: string[]) =>
    updateTags.mutate({ workflowId, tags: next });

  return (
    <>
      <PageHeader
        title="Entities"
        actions={
          <Link to={routes.EVALS_NEW} className="btn btn--primary btn--sm">
            New evaluation
          </Link>
        }
      />

      {list.isLoading ? (
        <Spinner />
      ) : list.error ? (
        <div className="alert alert--danger">Failed to load: {(list.error as Error).message}</div>
      ) : (
        <>
          {/* Org KPI cards — 5 cards per spec */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: 14,
              marginBottom: 20,
            }}
          >
            <KpiCard
              label="Overall Pass Rate"
              value={formatPct(orgKpis.overall)}
              sub={`across ${orgKpis.workflows} workflows`}
              color={passRateColor(orgKpis.overall)}
            />
            <KpiCard label="Workflows" value={String(orgKpis.workflows)} sub="with evaluations" />
            <KpiCard label="Total Runs" value={String(orgKpis.totalRuns)} sub="all time" />
            <KpiCard label="Runs 7d" value={String(orgKpis.runs7d)} sub="last 7 days" />
            <KpiCard
              label="Regressions 7d"
              value={String(orgKpis.regressions7d)}
              sub="gate failures"
              color={orgKpis.regressions7d > 0 ? 'var(--terminal-amber)' : undefined}
            />
          </div>

          {/* Entity type + Period filters */}
          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              marginBottom: 20,
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
            }}
          >
            <label
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 9,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'var(--text-secondary)',
              }}
            >
              Entity type
            </label>
            <select
              className="input"
              style={{ width: 140, height: 32, fontSize: 12 }}
              value={entityType}
              onChange={(e) => setEntityType(e.target.value as EntityType)}
            >
              <option value="all">All</option>
              <option value="workflows">Workflows</option>
              <option value="agents">Agents</option>
            </select>
            <label
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 9,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'var(--text-secondary)',
                marginLeft: 8,
              }}
            >
              Period
            </label>
            <select
              className="input"
              style={{ width: 140, height: 32, fontSize: 12 }}
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as DateRange)}
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="all">All time</option>
            </select>
          </div>

          {/* Tag filter (multi-select, ANY) + group-by-tag toggle */}
          {allTags.length > 0 && (
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                marginBottom: 20,
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 9,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'var(--text-secondary)',
                }}
              >
                Tags
              </span>
              {allTags.map((tag) => {
                const on = selectedTags.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    className={`btn btn--sm ${on ? 'btn--secondary' : 'btn--ghost'}`}
                    aria-pressed={on}
                    onClick={() => toggleTag(tag)}
                    style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}
                  >
                    {tag}
                  </button>
                );
              })}
              {selectedTags.length > 0 && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setSelectedTags([])}
                >
                  Clear
                </button>
              )}
              <button
                type="button"
                className={`btn btn--sm ${groupByTag ? 'btn--secondary' : 'btn--ghost'}`}
                aria-pressed={groupByTag}
                onClick={() => setGroupByTag((v) => !v)}
                style={{ marginLeft: 'auto' }}
              >
                Group by tag
              </button>
            </div>
          )}

          {/* Org-level trend chart */}
          {(() => {
            // Build trend series from all evals in range, sorted by date
            const sorted = [...evalsInRange].sort(
              (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
            );
            const trendPoints: ITrendPoint[] = sorted
              .map((e) => ({ date: new Date(e.createdAt), value: extractPassRate(e) }))
              .filter((p) => p.value !== null);
            if (trendPoints.length >= 2) {
              return (
                <TrendChart
                  series={[
                    {
                      name: 'Aggregated Pass Rate',
                      color: 'var(--horizon-glow)',
                      points: trendPoints,
                    },
                  ]}
                  title={`Aggregated Pass Rate Trend — ${dateRange === 'all' ? 'All time' : `Last ${dateRange}`}`}
                />
              );
            }
            return null;
          })()}

          {/* Entity table — flat, or sectioned when grouping by tag */}
          {visibleGroups.length === 0 ? (
            <div className="card" style={{ padding: 24 }}>
              <p className="t-muted">
                {entityType === 'agents'
                  ? 'No agent evaluations yet. Agent evaluation support is coming soon.'
                  : selectedTags.length > 0
                    ? 'No entities carry the selected tag(s).'
                    : 'No evaluations in this time range. Run an evaluation to populate this view.'}
              </p>
            </div>
          ) : groupByTag ? (
            <TagSections
              groups={visibleGroups}
              allTags={allTags}
              selectedTags={selectedTags}
              onEditTags={onEditTags}
              onTagClick={toggleTag}
            />
          ) : (
            <EntityTable groups={visibleGroups} onEditTags={onEditTags} onTagClick={toggleTag} />
          )}
        </>
      )}
    </>
  );
}

// ── Org-overview entity table ────────────────────────────────────────────────

interface IEntityTableProps {
  groups: IWorkflowGroup[];
  onEditTags: (workflowId: string, next: string[]) => void;
  onTagClick: (tag: string) => void;
}

/**
 * The one-row-per-workflow entity table. Extracted so the flat view and each
 * "group by tag" section render identical markup. Adds a Tags column (inline
 * editing on the workflow, click-to-filter) between Entity and Suites.
 */
function EntityTable({ groups, onEditTags, onTagClick }: IEntityTableProps) {
  return (
    <div className="card">
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 880 }}>
          <thead>
            <tr>
              <th style={thStyle}>Entity</th>
              <th style={thStyle}>Tags</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Suites</th>
              <th style={thStyle}>Latest Status</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Latest Pass Rate</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>pass@1</th>
              <th style={thStyle}>Trend</th>
              <th style={thStyle}>Last Run</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <EntityRow key={g.workflowId} g={g} onEditTags={onEditTags} onTagClick={onTagClick} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EntityRow({
  g,
  onEditTags,
  onTagClick,
}: {
  g: IWorkflowGroup;
  onEditTags: (workflowId: string, next: string[]) => void;
  onTagClick: (tag: string) => void;
}) {
  const nav = useNavigate();
  const isKg = g.workflowId === KG_GROUP_KEY;
  const isMemory = g.workflowId === MEMORY_GROUP_KEY;
  return (
    <tr
      onClick={() => nav({ to: '/evals', search: { workflowId: g.workflowId } })}
      style={{ cursor: 'pointer', borderTop: '1px solid var(--border-subtle)' }}
      onMouseEnter={(e) => {
        for (const td of e.currentTarget.children)
          (td as HTMLElement).style.background = 'rgba(44,65,66,0.08)';
      }}
      onMouseLeave={(e) => {
        for (const td of e.currentTarget.children) (td as HTMLElement).style.background = '';
      }}
    >
      <td style={tdStyle}>
        <div style={{ fontWeight: 500 }}>
          {isKg ? (
            <>
              <span style={{ color: 'var(--text-primary)' }}>Knowledge Graph Evals</span>
              <TypeBadge type="KG" color="var(--accent-violet, #7c5cff)" />
            </>
          ) : isMemory ? (
            <>
              <span style={{ color: 'var(--text-primary)' }}>Memory Evals</span>
              <TypeBadge type="Memory" color="var(--accent-teal, #2c9c8f)" />
            </>
          ) : (
            <>
              {g.workflowName || (
                <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  Unknown workflow
                </span>
              )}
              <TypeBadge type="Workflow" color="var(--horizon-glow)" />
            </>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
          {g.totalRuns} run{g.totalRuns !== 1 ? 's' : ''} total
        </div>
      </td>
      {/* Tags: editable on a real workflow, read-only placeholder for KG. */}
      <td style={{ ...tdStyle, maxWidth: 240 }} onClick={(e) => e.stopPropagation()}>
        {isKg || isMemory ? (
          <span className="t-muted">—</span>
        ) : (
          <TagEditor
            tags={g.tags}
            ariaLabel={`Tags for ${g.workflowName || 'workflow'}`}
            onChange={(next) => onEditTags(g.workflowId, next)}
            onTagClick={onTagClick}
          />
        )}
      </td>
      <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 13 }}>
        {g.suiteCount || '—'}
      </td>
      <td style={tdStyle}>
        {g.latestStatus ? (
          <span className={`fk-badge--${g.latestStatus.toLowerCase()}`}>{g.latestStatus}</span>
        ) : (
          <span className="t-muted">—</span>
        )}
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 13,
            fontWeight: 500,
            color: passRateColor(g.latestPassRate),
          }}
        >
          {formatPct(g.latestPassRate)}
        </span>
      </td>
      <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 13 }}>
        {formatScore(g.latestPassAt1)}
      </td>
      <td style={tdStyle}>
        <MiniSparkline values={g.timeline.map((t) => t.passRate)} />
      </td>
      <td style={{ ...tdStyle, color: 'var(--text-secondary)', fontSize: 12 }}>
        {g.latestCreatedAt ? formatDateTime(g.latestCreatedAt) : '—'}
      </td>
    </tr>
  );
}

/**
 * "Group by tag" layout: one collapsible `<details>` section per tag (plus an
 * "Untagged" bucket), each wrapping a full {@link EntityTable}. A workflow with
 * multiple tags appears under each of its tags. Only non-empty sections render.
 */
function TagSections({
  groups,
  allTags,
  selectedTags,
  onEditTags,
  onTagClick,
}: {
  groups: IWorkflowGroup[];
  allTags: string[];
  selectedTags: string[];
  onEditTags: (workflowId: string, next: string[]) => void;
  onTagClick: (tag: string) => void;
}) {
  // When a tag filter is active, only section by the selected tags; otherwise
  // section by every tag present among the visible groups.
  const sectionTags = (selectedTags.length > 0 ? selectedTags : allTags).filter((tag) =>
    groups.some((g) => g.tags.includes(tag)),
  );
  const untagged = groups.filter((g) => g.tags.length === 0);

  const sections: Array<{ label: string; groups: IWorkflowGroup[] }> = sectionTags.map((tag) => ({
    label: tag,
    groups: groups.filter((g) => g.tags.includes(tag)),
  }));
  if (untagged.length > 0) sections.push({ label: 'Untagged', groups: untagged });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {sections.map((section) => (
        <details key={section.label} open={true}>
          <summary
            style={{
              cursor: 'pointer',
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--fog-green)',
              marginBottom: 10,
              userSelect: 'none',
            }}
          >
            {section.label}
            <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>
              {section.groups.length}
            </span>
          </summary>
          <EntityTable groups={section.groups} onEditTags={onEditTags} onTagClick={onTagClick} />
        </details>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// LEVEL 2 — Workflow Evals
// ═══════════════════════════════════════════════════════════════════════════

// eslint-disable-next-line @typescript-eslint/naming-convention -- synthetic routing key, not a variable
const KG_GROUP_KEY = '__kg__';
// eslint-disable-next-line @typescript-eslint/naming-convention -- synthetic routing key, not a variable
const MEMORY_GROUP_KEY = '__memory__';

function WorkflowEvalsView({ workflowId }: { workflowId: string }) {
  const client = useClient();
  const nav = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [suiteFilter, setSuiteFilter] = useState<string>('all');
  // Default to all-time: eval runs are infrequent, so a 30-day default made a
  // workflow whose last run is >30 days old look like it had zero runs
  // ("No eval runs match the current filters"). Show the full history by
  // default; users can still narrow via the period dropdown.
  const [dateRange, setDateRange] = useState<DateRange>('all');

  const isKgGroup = workflowId === KG_GROUP_KEY;
  const isMemoryGroup = workflowId === MEMORY_GROUP_KEY;
  const isSyntheticGroup = isKgGroup || isMemoryGroup;

  const list = useQuery({
    queryKey: ['evaluations', { workflowId }],
    queryFn: () =>
      isSyntheticGroup
        ? client.http
            .get('v1/evaluations?limit=200')
            .json<IListEnvelope<EvaluationListItem>>()
            .then((d) => ({
              ...d,
              items: (d.items ?? []).filter(
                (e) => !e.workflowId && (e.targetKind === 'MEMORY') === isMemoryGroup,
              ),
            }))
        : client.http
            .get(`v1/evaluations?workflowId=${workflowId}&limit=200`)
            .json<IListEnvelope<EvaluationListItem>>(),
  });
  const workflow = useQuery({
    enabled: !isSyntheticGroup,
    queryKey: ['workflow', workflowId],
    queryFn: () => client.http.get(`v1/workflows/${workflowId}`).json<WorkflowListItem>(),
  });
  const updateTags = useUpdateWorkflowTags();
  const versions = useQuery({
    enabled: !isSyntheticGroup,
    queryKey: ['workflow-versions', workflowId],
    queryFn: () => client.http.get(`v1/workflows/${workflowId}/versions`).json<WorkflowVersion[]>(),
  });
  const versionLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const v of versions.data ?? []) m.set(v.id, `v${v.versionNumber}`);
    return (id: string | null | undefined): string | null => {
      if (!id) return null;
      return m.get(id) ?? id.slice(0, 12);
    };
  }, [versions.data]);

  const evals = useMemo(() => {
    const items = filterByDateRange(list.data?.items ?? [], dateRange);
    return [...items].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [list.data, dateRange]);

  // Unique suite IDs for filter dropdown
  const suiteIds = useMemo(() => {
    const ids = new Set<string>();
    for (const e of evals) {
      const sid = getSuiteId(e);
      if (sid) ids.add(sid);
    }
    return [...ids];
  }, [evals]);

  const filtered = useMemo(() => {
    let result = evals;
    if (statusFilter !== 'all') {
      result = result.filter((e) => e.status.toLowerCase() === statusFilter);
    }
    if (suiteFilter !== 'all') {
      result = result.filter((e) => getSuiteId(e) === suiteFilter);
    }
    return result;
  }, [evals, statusFilter, suiteFilter]);

  // Workflow KPIs — compute from all evals (not filtered)
  const kpis = useMemo(() => {
    const passRates = evals.map(extractPassRate).filter((v): v is number => v !== null);
    const passAt1s = evals.map(extractPassAt1).filter((v): v is number => v !== null);
    const overall =
      passRates.length > 0 ? passRates.reduce((a, b) => a + b, 0) / passRates.length : null;
    const latest = passRates[0] ?? null;
    const meanPassAt1 =
      passAt1s.length > 0 ? passAt1s.reduce((a, b) => a + b, 0) / passAt1s.length : null;
    return { overall, latest, totalRuns: evals.length, meanPassAt1 };
  }, [evals]);

  const statusCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const e of evals) {
      const s = e.status.toLowerCase();
      c[s] = (c[s] ?? 0) + 1;
    }
    return c;
  }, [evals]);

  return (
    <>
      <PageHeader
        title={
          isKgGroup
            ? 'Knowledge Graph Evals'
            : isMemoryGroup
              ? 'Memory Evals'
              : (workflow.data?.name ?? workflowId.slice(0, 12))
        }
        actions={
          <Link to={routes.EVALS_NEW} className="btn btn--primary btn--sm">
            New evaluation
          </Link>
        }
      />

      {list.isLoading ? (
        <Spinner />
      ) : list.error ? (
        <div className="alert alert--danger">Failed to load: {(list.error as Error).message}</div>
      ) : (
        <>
          {/* Workflow tags — inline editable in the header area (source of
              truth is the Workflow entity). Hidden for the KG pseudo-group. */}
          {!isSyntheticGroup && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 16,
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 9,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'var(--text-secondary)',
                }}
              >
                Tags
              </span>
              <TagEditor
                tags={workflow.data?.tags ?? []}
                ariaLabel="Workflow tags"
                onChange={(next) => updateTags.mutate({ workflowId, tags: next })}
              />
            </div>
          )}

          {/* Workflow KPI cards — 4 cards */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 14,
              marginBottom: 20,
            }}
          >
            <KpiCard
              label="Latest Pass Rate"
              value={formatPct(kpis.latest)}
              color={passRateColor(kpis.latest)}
            />
            <KpiCard
              label="Avg Pass Rate"
              value={formatPct(kpis.overall)}
              color={passRateColor(kpis.overall)}
            />
            <KpiCard
              label="Mean pass@1"
              value={formatScore(kpis.meanPassAt1)}
              sub="per-trial reliability"
            />
            <KpiCard label="Total Runs" value={String(kpis.totalRuns)} />
          </div>

          {/* Filter row: status + suite + date range */}
          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              marginBottom: 16,
              flexWrap: 'wrap',
            }}
          >
            {/* Status filter */}
            <div style={{ display: 'flex', gap: 2 }}>
              {['all', 'completed', 'running', 'failed', 'pending', 'cancelled'].map((s) => {
                const count = s === 'all' ? evals.length : (statusCounts[s] ?? 0);
                if (s !== 'all' && count === 0) return null;
                return (
                  <button
                    key={s}
                    className={`btn btn--sm ${statusFilter === s ? 'btn--secondary' : 'btn--ghost'}`}
                    onClick={() => setStatusFilter(s)}
                    style={{ textTransform: 'capitalize' }}
                  >
                    {s} <span style={{ opacity: 0.6, marginLeft: 4 }}>{count}</span>
                  </button>
                );
              })}
            </div>

            {/* Suite + date range filters (right-aligned) */}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
              {suiteIds.length > 0 && (
                <select
                  className="input"
                  style={{ width: 160, height: 32, fontSize: 12 }}
                  value={suiteFilter}
                  onChange={(e) => setSuiteFilter(e.target.value)}
                >
                  <option value="all">All Suites</option>
                  {suiteIds.map((sid) => (
                    <option key={sid} value={sid}>
                      {sid.slice(0, 16)}
                    </option>
                  ))}
                </select>
              )}
              <select
                className="input"
                style={{ width: 130, height: 32, fontSize: 12 }}
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value as DateRange)}
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="all">All time</option>
              </select>
            </div>
          </div>

          {/* Per-suite trend chart */}
          {(() => {
            // Group evals by suiteId, build one series per suite
            const bySuite = new Map<string, ITrendPoint[]>();
            const unsuitedPoints: ITrendPoint[] = [];
            for (const e of evals) {
              const pr = extractPassRate(e);
              if (pr === null) continue;
              const sid = getSuiteId(e);
              const point: ITrendPoint = { date: new Date(e.createdAt), value: pr };
              if (sid) {
                const arr = bySuite.get(sid) ?? [];
                arr.push(point);
                bySuite.set(sid, arr);
              } else {
                unsuitedPoints.push(point);
              }
            }
            const seriesColors = [
              'var(--horizon-glow)',
              'var(--interface-iris)',
              'var(--terminal-amber)',
              'var(--fog-green)',
            ];
            const allSeries: ITrendSeries[] = [];
            let ci = 0;
            for (const [sid, points] of bySuite) {
              points.sort((a, b) => a.date.getTime() - b.date.getTime());
              allSeries.push({
                name: sid.slice(0, 16),
                color: seriesColors[ci % seriesColors.length] ?? 'var(--text-secondary)',
                points,
              });
              ci++;
            }
            if (unsuitedPoints.length >= 2 && allSeries.length === 0) {
              unsuitedPoints.sort((a, b) => a.date.getTime() - b.date.getTime());
              allSeries.push({
                name: 'All Runs',
                color: 'var(--horizon-glow)',
                points: unsuitedPoints,
              });
            }
            const totalPoints = allSeries.reduce((sum, s) => sum + s.points.length, 0);
            if (totalPoints >= 2) {
              return (
                <TrendChart
                  series={allSeries}
                  title={`Pass Rate Trend — ${workflow.data?.name ?? 'Workflow'} — ${dateRange === 'all' ? 'All time' : `Last ${dateRange}`}`}
                  gateThreshold={0.9}
                />
              );
            }
            return null;
          })()}

          {/* Runs table */}
          {filtered.length === 0 ? (
            <div className="card" style={{ padding: 24 }}>
              {/* Distinguish "genuinely no runs" from "runs exist but the date
                  filter hid them" — the latter is the confusing case (KPIs read
                  0 while history exists). Offer a one-click widen. */}
              {dateRange !== 'all' && (list.data?.items.length ?? 0) > 0 ? (
                <p className="t-muted">
                  No eval runs in the selected period. {list.data?.items.length ?? 0} run
                  {(list.data?.items.length ?? 0) !== 1 ? 's' : ''} exist outside it —{' '}
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    style={{ padding: '0 4px' }}
                    onClick={() => setDateRange('all')}
                  >
                    show all time
                  </button>
                  .
                </p>
              ) : (
                <p className="t-muted">No eval runs match the current filters.</p>
              )}
            </div>
          ) : (
            <div className="card">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Run</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Version</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Tasks</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Pass Rate</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>pass@1</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Duration</th>
                    <th style={thStyle}>Started</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((e) => {
                    const pr = extractPassRate(e);
                    const pa1 = extractPassAt1(e);
                    const tasks = extractTaskCount(e);
                    return (
                      <tr
                        key={e.id}
                        onClick={() => nav({ to: routes.evalDetail(e.id) })}
                        style={{ cursor: 'pointer', borderTop: '1px solid var(--border-subtle)' }}
                        onMouseEnter={(ev) => {
                          for (const td of ev.currentTarget.children)
                            (td as HTMLElement).style.background = 'rgba(44,65,66,0.08)';
                        }}
                        onMouseLeave={(ev) => {
                          for (const td of ev.currentTarget.children)
                            (td as HTMLElement).style.background = '';
                        }}
                      >
                        <td style={tdStyle}>
                          <div
                            style={{
                              fontFamily: 'var(--font-mono)',
                              fontSize: 12,
                              color: 'var(--horizon-glow)',
                            }}
                          >
                            {e.id.slice(0, 12)}
                          </div>
                          {getSuiteId(e) && (
                            <div
                              style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}
                            >
                              Suite: {(getSuiteId(e) ?? '').slice(0, 12)}
                            </div>
                          )}
                        </td>
                        <td style={tdStyle}>
                          <span className={`fk-badge--${e.status.toLowerCase()}`}>{e.status}</span>
                        </td>
                        <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                          {versionLabel(e.workflowVersionId) ?? (
                            <span className="t-muted">Current</span>
                          )}
                        </td>
                        <td
                          style={{
                            ...tdStyle,
                            textAlign: 'right',
                            fontFamily: 'var(--font-mono)',
                            fontSize: 13,
                          }}
                        >
                          {tasks ? `${tasks.passed}/${tasks.total}` : '—'}
                        </td>
                        <td
                          style={{
                            ...tdStyle,
                            textAlign: 'right',
                            fontFamily: 'var(--font-mono)',
                            fontSize: 13,
                            fontWeight: 500,
                            color: passRateColor(pr),
                          }}
                        >
                          {formatPct(pr)}
                        </td>
                        <td
                          style={{
                            ...tdStyle,
                            textAlign: 'right',
                            fontFamily: 'var(--font-mono)',
                            fontSize: 13,
                          }}
                        >
                          {formatScore(pa1)}
                        </td>
                        <td
                          style={{
                            ...tdStyle,
                            textAlign: 'right',
                            fontFamily: 'var(--font-mono)',
                            fontSize: 12,
                          }}
                        >
                          {formatDuration(e.startedAt, e.finishedAt)}
                        </td>
                        <td style={{ ...tdStyle, color: 'var(--text-secondary)', fontSize: 12 }}>
                          {formatDateTime(e.createdAt)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}

// ── Shared components ───────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub?: string;
  color?: string;
}) {
  return (
    <div className="card" style={{ padding: '18px 20px' }}>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 9,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'var(--fog-green)',
          marginBottom: 10,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 500,
          fontSize: 28,
          color: color ?? 'var(--text-primary)',
          letterSpacing: '-0.02em',
          lineHeight: 1,
        }}
      >
        {value}
      </div>
      {sub && (
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            color: 'var(--text-secondary)',
            marginTop: 7,
          }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

function TypeBadge({ type, color }: { type: string; color: string }) {
  return (
    <span
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 8,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color,
        border: `1px solid ${color}`,
        borderRadius: 2,
        padding: '1px 4px',
        marginLeft: 8,
        verticalAlign: 'middle',
      }}
    >
      {type}
    </span>
  );
}
